import React, { useState, useEffect, useMemo } from 'react';
import { normalizePersistedQuoteEditorState } from '@shared/quote-editor-state';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Send, CheckCircle, Cloud, CloudOff, Plus, FolderOpen, Package, FileText, Euro } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { useOfferStore } from '@/lib/store';
import { saveQuoteData } from '@/lib/quote-data-service';
import {
  bindQuotePersistenceResponse,
  buildQuoteSaveRequest,
} from '@/lib/quote-save-coordinator';
import { getLineBaseNet, getLineNetAfterDiscount, normalizeVatRate } from '@/lib/quote-calculations';

interface SoftecStatus {
  enabled: boolean;
  configured: boolean;
  env?: string;
  connected?: boolean;
  message?: string;
}

interface SoftecExportResult {
  success: boolean;
  softecEnv: string;
  message: string;
  error?: string;
  data?: {
    projectId?: string;
    budgetId?: string;
    warnings?: string[];
  };
}

interface SoftecProject {
  id: string;
  name: string;
  description?: string;
  location?: string;
  clientName?: string;
  status?: string;
  createdAt?: string;
}

interface ExistingBudget {
  id: string;
  sourceOfferId?: string;
  sourceOfferVersion?: string;
  status?: string; // 'draft' | 'active' | 'superseded'
  grossTotal?: string;
  createdAt?: string;
}

interface WorkData {
  hasWork: boolean;
  expenseCount: number;
  assignmentCount: number;
  timeEntryCount: number;
  taskCount: number;
}

interface BudgetCheckResult {
  projectId: string;
  budgets: ExistingBudget[];
  count: number;
  hasBudgets: boolean;
  hasActiveBudget: boolean;
  activeBudget: ExistingBudget | null;
  workData: WorkData;
}

type ExportMode = 'create' | 'existing';

interface SoftecExportButtonProps {
  slot: number;
  disabled?: boolean;
  className?: string;
}

export function SoftecExportButton({ slot, disabled, className }: SoftecExportButtonProps) {
  const { toast } = useToast();
  const quoteItems = useOfferStore((state) => state.quoteItems);
  const quoteHeader = useOfferStore((state) => state.quoteHeader);
  const discountAmount = useOfferStore((state) => state.discountAmount);
  const discountType = useOfferStore((state) => state.discountType);
  const discountApplicationMode = useOfferStore((state) => state.discountApplicationMode);
  const isDiscountAppliedToPrices = useOfferStore((state) => state.isDiscountAppliedToPrices);
  const [status, setStatus] = useState<SoftecStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [lastExportResult, setLastExportResult] = useState<SoftecExportResult | null>(null);

  // New state for export mode and project selection
  const [exportMode, setExportMode] = useState<ExportMode>('create');
  const [projects, setProjects] = useState<SoftecProject[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [existingStructureLabel, setExistingStructureLabel] = useState<string>('');
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);

  // Overwrite protection state
  const [existingBudgets, setExistingBudgets] = useState<ExistingBudget[]>([]);
  const [isCheckingBudgets, setIsCheckingBudgets] = useState(false);
  const [workData, setWorkData] = useState<WorkData | null>(null);

  // New project creation state
  const [projectTitle, setProjectTitle] = useState<string>('');
  const [previewData, setPreviewData] = useState<{
    positionCount: number;
    tradeCount: number;
    netTotal: number;
    grossTotal: number;
    offerNr: string;
    kunde: string;
    // GAEB hierarchy info
    coarseTradeCount: number;    // Level 0 trades (01, 02, 03)
    granularTradeCount: number;  // Level 1 trades (01.01, 01.02, 02.01)
    hasGAEBHierarchy: boolean;   // Whether data has GAEB structure
    coarseTradeNames: string[];  // Names of level 0 trades
    granularTradeNames: string[]; // Names of level 1 trades
  } | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // GAEB hierarchy mode: 'coarse' = Level 0 as trades, 'granular' = Level 1 as trades
  type HierarchyMode = 'coarse' | 'granular';
  const [hierarchyMode, setHierarchyMode] = useState<HierarchyMode>('granular');

  // Load Softec status
  const loadStatus = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/softec/status');
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      } else {
        setStatus({ enabled: false, configured: false, env: undefined });
      }
    } catch (error) {
      console.error('Failed to load Softec status:', error);
      setStatus({ enabled: false, configured: false, env: undefined });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  // Brücken-Vertrag aus der PM-App (Nachtrag): kommt der Editor mit
  // ?projectId=… bzw. ?softecProjectId=… (SSO-Sprung mode=nachtrag), ist das
  // Zielprojekt bekannt → Export-Modus "In bestehendes Projekt" vorbelegen.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const prefillProjectId = params.get('projectId')?.trim() || params.get('softecProjectId')?.trim();
    if (prefillProjectId) {
      setExportMode('existing');
      setSelectedProjectId(prefillProjectId);
    }
  }, []);

  // Load preview data when dialog opens — auto-saves first so server has current data
  const loadPreview = async () => {
    setIsLoadingPreview(true);
    setPreviewError(null);
    let previewSlot = slot;

    if (!slot || slot < 1) {
      setPreviewError('Angebot muss zuerst gespeichert werden');
      setIsLoadingPreview(false);
      return;
    }

    try {
      // Auto-save current editor state so the server has up-to-date data
      try {
        const storeState = useOfferStore.getState();
        const {
          quoteHeader,
          quoteItems,
          positions,
          gewerkOrder,
          currentQuoteSlot,
          currentQuoteStorageRevision,
          setCurrentQuotePersistence,
        } = storeState;
        if (quoteItems.length > 0) {
          if (currentQuoteSlot === null) {
            throw new Error('Persistenzidentität des Angebots fehlt.');
          }
          const editorState = normalizePersistedQuoteEditorState(storeState);
          const result = await saveQuoteData(
            buildQuoteSaveRequest(
              {
                header: quoteHeader,
                items: quoteItems,
                positions,
                gewerkOrder: gewerkOrder as string[],
                editorState,
              },
              {
                slot: currentQuoteSlot,
                storageRevision: currentQuoteStorageRevision ?? 0,
              },
              'save',
            ),
          );
          const identity = bindQuotePersistenceResponse(
            result,
            setCurrentQuotePersistence,
          );
          previewSlot = identity.slot;
        }
      } catch (saveErr) {
        console.warn('Auto-save before preview failed:', saveErr);
        setPreviewError(
          saveErr instanceof Error
            ? saveErr.message
            : 'Angebot konnte vor der Vorschau nicht gespeichert werden.',
        );
        return;
      }

      const response = await fetch('/api/softec/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot: previewSlot }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.preview) {
          const preview = data.preview;
          const trades = preview.trades || [];
          const raw = data.rawStats;

          // Use raw stats for display (includes all items, not just menge > 0)
          // Fall back to transformed data if rawStats not available
          const positionCount = raw?.positionCount ?? trades.reduce(
            (sum: number, trade: any) => sum + (trade.positions?.length || 0), 0
          );

          // Coarse trade info from raw stats (all gewerke, not filtered)
          const coarseTradeNames: string[] = raw?.tradeNames ?? trades.map((t: any) => t.name);
          const coarseTradeCount = raw?.tradeCount ?? trades.length;

          // Granular trades: extract from position numbers in transformed data
          const granularTradeMap = new Map<string, string>();
          let hasGAEBHierarchy = false;

          trades.forEach((trade: any) => {
            (trade.positions || []).forEach((pos: any) => {
              if (pos.nr) {
                const parts = pos.nr.split('.');
                if (parts.length >= 2) {
                  hasGAEBHierarchy = true;
                  const untergewerkNr = `${parts[0]}.${parts[1]}`;
                  if (!granularTradeMap.has(untergewerkNr)) {
                    granularTradeMap.set(untergewerkNr, `${untergewerkNr} (${trade.name})`);
                  }
                }
              }
            });
          });

          const granularTradeNames: string[] = Array.from(granularTradeMap.keys()).sort();
          const granularTradeCount = hasGAEBHierarchy ? granularTradeMap.size : coarseTradeCount;

          // Use raw totals (all items) for display, not filtered totals
          const grossTotal = raw?.gross ?? preview.totals?.gross ?? 0;
          const netTotal = raw?.net ?? preview.totals?.net ?? 0;

          setPreviewData({
            positionCount,
            tradeCount: coarseTradeCount,
            netTotal,
            grossTotal,
            offerNr: preview.id || '',
            kunde: preview.projectMeta?.clientName || '',
            coarseTradeCount,
            granularTradeCount,
            hasGAEBHierarchy,
            coarseTradeNames: coarseTradeNames.slice(0, 5),
            granularTradeNames: granularTradeNames.slice(0, 5),
          });

          // Pre-fill project title with bauvorhaben if available
          if (!projectTitle && preview.projectMeta?.name) {
            setProjectTitle(preview.projectMeta.name);
          }
        }
      } else {
        const errorData = await response.json().catch(() => null);
        setPreviewError(errorData?.error || `Vorschau konnte nicht geladen werden (${response.status})`);
      }
    } catch (error) {
      console.error('Failed to load preview:', error);
      setPreviewError('Vorschau konnte nicht geladen werden');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Load preview when dialog opens
  useEffect(() => {
    if (showConfirmDialog) {
      loadPreview();
    }
  }, [showConfirmDialog, slot]);

  // Load projects when dialog opens and mode is "existing"
  const loadProjects = async () => {
    setIsLoadingProjects(true);
    try {
      const response = await fetch('/api/softec-db/projects?limit=50');
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      } else {
        setProjects([]);
        toast({
          title: 'Projekte konnten nicht geladen werden',
          description: 'Bitte prüfe die Softec-DB Konfiguration',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
      setProjects([]);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  useEffect(() => {
    if (showConfirmDialog && exportMode === 'existing' && projects.length === 0) {
      loadProjects();
    }
  }, [showConfirmDialog, exportMode]);

  // Check for existing budgets when project is selected
  const checkProjectBudgets = async (projectId: string) => {
    setIsCheckingBudgets(true);
    setExistingBudgets([]);
    setWorkData(null);

    try {
      const response = await fetch(`/api/softec-db/projects/${projectId}/budgets`);
      if (response.ok) {
        const data: BudgetCheckResult = await response.json();
        setExistingBudgets(data.budgets || []);
        setWorkData(data.workData || null);
      }
    } catch (error) {
      console.error('Failed to check project budgets:', error);
      // Don't block on error - just proceed without warning
    } finally {
      setIsCheckingBudgets(false);
    }
  };

  // Trigger budget check when project selection changes
  useEffect(() => {
    if (selectedProjectId && exportMode === 'existing') {
      checkProjectBudgets(selectedProjectId);
    } else {
      setExistingBudgets([]);
      setWorkData(null);
      if (exportMode === 'existing' && !selectedProjectId) {
        setExistingStructureLabel('');
      }
    }
  }, [selectedProjectId, exportMode]);

  // Keep editable structure label aligned with selected project as default.
  // Do not force-reset after the user edits the field manually.
  useEffect(() => {
    if (exportMode !== 'existing' || !selectedProjectId) {
      return;
    }

    const selectedProject = projects.find((project) => project.id === selectedProjectId);
    if (!selectedProject?.name) {
      return;
    }

    setExistingStructureLabel((currentLabel) =>
      currentLabel.trim().length > 0 ? currentLabel : selectedProject.name
    );
  }, [selectedProjectId, exportMode, projects]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!showConfirmDialog) {
      setExistingBudgets([]);
      setWorkData(null);
      setPreviewData(null);
      setPreviewError(null);
      setHierarchyMode('granular');
      setExistingStructureLabel('');
      // Don't reset projectTitle - preserve for re-opening
    }
  }, [showConfirmDialog]);

  const previewTotals = useMemo(() => {
    if (!previewData) return null;

    const vatRate = normalizeVatRate(quoteHeader?.mwstSatz);
    const hasItems = Array.isArray(quoteItems) && quoteItems.length > 0;

    const baseNet = hasItems
      ? quoteItems.reduce((sum, item) => sum + getLineBaseNet(item), 0)
      : previewData.netTotal;
    const lineNet = hasItems
      ? quoteItems.reduce((sum, item) => sum + getLineNetAfterDiscount(item), 0)
      : previewData.netTotal;

    const lineDiscount = Math.max(0, Math.round((baseNet - lineNet) * 100) / 100);

    const hasGlobalTotalDiscount =
      discountApplicationMode === 'total' &&
      !isDiscountAppliedToPrices &&
      discountAmount > 0;

    const globalDiscountRaw = hasGlobalTotalDiscount
      ? (discountType === 'percent' ? (lineNet * discountAmount) / 100 : discountAmount)
      : 0;
    const globalDiscount = Math.max(0, Math.min(lineNet, Math.round(globalDiscountRaw * 100) / 100));

    const netAfterAllDiscounts = Math.max(0, Math.round((lineNet - globalDiscount) * 100) / 100);
    const vatAmount = Math.round(netAfterAllDiscounts * (vatRate / 100) * 100) / 100;
    const grossAfterAllDiscounts = Math.round((netAfterAllDiscounts + vatAmount) * 100) / 100;
    const totalDiscount = Math.max(0, Math.round((lineDiscount + globalDiscount) * 100) / 100);

    return {
      baseNet,
      lineDiscount,
      globalDiscount,
      totalDiscount,
      netAfterAllDiscounts,
      grossAfterAllDiscounts,
      hasAnyDiscount: totalDiscount > 0,
      hasGlobalTotalDiscount,
    };
  }, [
    previewData,
    quoteHeader?.mwstSatz,
    quoteItems,
    discountAmount,
    discountType,
    discountApplicationMode,
    isDiscountAppliedToPrices,
  ]);

  // Don't render if Softec is not enabled
  if (!status?.enabled) {
    return null;
  }

  const handleExportClick = async () => {
    if (!status?.configured) {
      toast({
        title: 'Softec nicht konfiguriert',
        description: 'Die Softec-Integration ist nicht vollständig konfiguriert.',
        variant: 'destructive',
      });
      return;
    }

    // If slot is not yet assigned (e.g. after GAEB conversion), trigger a save first
    let effectiveSlot = slot;
    if (!effectiveSlot || effectiveSlot < 1) {
      try {
        const storeState = useOfferStore.getState();
        const {
          quoteHeader,
          quoteItems,
          positions,
          gewerkOrder,
          currentQuoteSlot,
          currentQuoteStorageRevision,
          setCurrentQuotePersistence,
        } = storeState;
        if (quoteItems.length > 0) {
          const editorState = normalizePersistedQuoteEditorState(storeState);
          const result = await saveQuoteData(
            buildQuoteSaveRequest(
              {
                header: quoteHeader,
                items: quoteItems,
                positions,
                gewerkOrder: gewerkOrder as string[],
                editorState,
              },
              currentQuoteSlot === null
                ? null
                : {
                    slot: currentQuoteSlot,
                    storageRevision: currentQuoteStorageRevision ?? 0,
                  },
              'save',
            ),
          );
          effectiveSlot = bindQuotePersistenceResponse(
            result,
            setCurrentQuotePersistence,
          ).slot;
        }
      } catch (err) {
        console.warn('Auto-save before export failed:', err);
      }

      if (!effectiveSlot || effectiveSlot < 1) {
        toast({
          title: 'Bitte zuerst speichern',
          description: 'Das Angebot muss gespeichert sein, bevor es an Softec gesendet werden kann.',
          variant: 'destructive',
        });
        return;
      }
    }

    setShowConfirmDialog(true);
  };

  const handleConfirmExport = async () => {
    // Validate for "existing" mode
    if (exportMode === 'existing' && !selectedProjectId) {
      toast({
        title: 'Kein Projekt ausgewählt',
        description: 'Bitte wähle ein Projekt aus oder erstelle ein neues.',
        variant: 'destructive',
      });
      return;
    }

    setShowConfirmDialog(false);
    setIsExporting(true);
    setLastExportResult(null);

    // Read global discount from store — only relevant in "total" mode when not baked into prices
    const {
      discountAmount: storeDiscountAmount,
      discountType: storeDiscountType,
      discountApplicationMode: storeDiscountMode,
      isDiscountAppliedToPrices: storeDiscountApplied,
    } = useOfferStore.getState();
    const globalDiscount =
      storeDiscountMode === 'total' && !storeDiscountApplied && storeDiscountAmount > 0
        ? { amount: storeDiscountAmount, type: storeDiscountType }
        : undefined;

    try {
      if (exportMode === 'create') {
        // Smart project creation: prepare pending import + redirect to Softec
        const response = await fetch('/api/softec/prepare-import', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slot, projectTitle: projectTitle.trim() || undefined, hierarchyMode, globalDiscount }),
        });

        const result = await response.json();

        if (result.success && result.redirectUrl) {
          toast({
            title: 'Weiterleitung zu Softec',
            description: 'Projekt-Erstellung wird in Softec geöffnet...',
          });
          // Open Softec project creation in new tab with prefilled data
          window.open(result.redirectUrl, '_blank');
          setSelectedProjectId(null);
          setExportMode('create');
          setProjectTitle('');
          setShowConfirmDialog(false);
        } else {
          // Fallback to old create-project if prepare-import fails
          toast({
            title: 'Hinweis',
            description: result.error || 'Konnte Smart-Import nicht vorbereiten, erstelle Projekt direkt...',
          });
          const fallbackRes = await fetch('/api/softec/create-project', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ slot, projectTitle: projectTitle.trim() || undefined, hierarchyMode, globalDiscount }),
          });
          const fallbackResult = await fallbackRes.json();
          setLastExportResult(fallbackResult);
          if (fallbackResult.success) {
            toast({ title: 'Projekt erstellt', description: fallbackResult.message });
            // Redirect to the created project in Softec
            const projectId = fallbackResult.data?.projectId;
            const env = fallbackResult.softecEnv || 'dev';
            if (projectId) {
              const baseUrl = env === 'prod'
                ? 'https://app.fortuna-softec.com'
                : 'https://dev.fortuna-softec.com';
              window.open(`${baseUrl}/projects/${projectId}`, '_blank');
            }
            setSelectedProjectId(null);
            setExportMode('create');
            setProjectTitle('');
          } else {
            toast({ title: 'Export fehlgeschlagen', description: fallbackResult.error, variant: 'destructive' });
          }
        }
      } else {
        // Export to existing project
        const response = await fetch('/api/softec/export', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            slot,
            projectId: selectedProjectId,
            hierarchyMode,
            structureLabel: existingStructureLabel.trim() || undefined,
            globalDiscount,
          }),
        });

        const result: SoftecExportResult = await response.json();
        setLastExportResult(result);

        if (result.success) {
          toast({ title: 'Export erfolgreich', description: result.message });
          setSelectedProjectId(null);
          setExistingStructureLabel('');
          setExportMode('create');
          setProjectTitle('');
        } else {
          toast({ title: 'Export fehlgeschlagen', description: result.error || result.message, variant: 'destructive' });
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      toast({
        title: 'Export fehlgeschlagen',
        description: errorMessage,
        variant: 'destructive',
      });
      setLastExportResult({
        success: false,
        softecEnv: status?.env || 'dev',
        message: 'Export fehlgeschlagen',
        error: errorMessage,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportClick}
              disabled={disabled || isLoading || isExporting || !status?.configured}
              className={className}
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : lastExportResult?.success ? (
                <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
              ) : status?.connected ? (
                <Cloud className="h-4 w-4 mr-2" />
              ) : (
                <CloudOff className="h-4 w-4 mr-2 text-muted-foreground" />
              )}
              <span className="hidden sm:inline">An Softec senden</span>
              <span className="sm:hidden">Softec</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {!status?.configured
                ? 'Softec-Integration nicht konfiguriert'
                : slot === 0
                  ? 'Angebot wird gespeichert... Bitte kurz warten.'
                  : 'Angebot an Softec exportieren'}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              An Softec senden
            </DialogTitle>
            <DialogDescription>
              Das Angebot wird an die Softec-Umgebung gesendet und dort als neues Projekt-Budget angelegt.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
            {/* Preview Overview */}
            {previewData ? (
              <div className="rounded-lg border p-4 bg-blue-50/50 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-blue-900">
                  <FileText className="h-4 w-4" />
                  <span>Angebot {previewData.offerNr}</span>
                  {previewData.kunde && (
                    <span className="text-blue-600 ml-auto">{previewData.kunde}</span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="p-2 bg-white rounded border">
                    <div className="text-lg font-semibold text-blue-900">
                      {hierarchyMode === 'coarse' ? previewData.coarseTradeCount : previewData.granularTradeCount}
                    </div>
                    <div className="text-xs text-blue-600">
                      {(hierarchyMode === 'coarse' ? previewData.coarseTradeCount : previewData.granularTradeCount) === 1 ? 'Gewerk' : 'Gewerke'}
                    </div>
                  </div>
                  <div className="p-2 bg-white rounded border">
                    <div className="text-lg font-semibold text-blue-900">{previewData.positionCount}</div>
                    <div className="text-xs text-blue-600">Positionen</div>
                  </div>
                  <div className="p-2 bg-white rounded border">
                    <div className="text-lg font-semibold text-green-700">
                      {(previewTotals?.grossAfterAllDiscounts ?? previewData.grossTotal).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                    </div>
                    <div className="text-xs text-green-600">Brutto</div>
                  </div>
                </div>
                {previewTotals?.hasAnyDiscount && (
                  <div className="rounded border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-800">
                    <div className="flex items-center justify-between">
                      <span>Rabatt gesamt</span>
                      <span className="font-semibold">-{previewTotals.totalDiscount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</span>
                    </div>
                    <div className="mt-1 text-emerald-700/90">
                      Netto nach Rabatt: {previewTotals.netAfterAllDiscounts.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                    </div>
                  </div>
                )}
              </div>
            ) : previewError ? (
              <div className="text-sm text-amber-600 p-3 bg-amber-50 rounded border border-amber-200">
                {previewError}
              </div>
            ) : !isLoadingPreview ? (
              <div className="text-sm text-muted-foreground p-3">Vorschau nicht verfügbar</div>
            ) : null}

            {/* GAEB Hierarchy Mode Selection - only show if data has hierarchy */}
            {previewData?.hasGAEBHierarchy && (
              <div className="rounded-lg border p-4 space-y-3">
                <Label className="text-sm font-medium">Gewerke-Struktur in Softec</Label>
                <p className="text-xs text-muted-foreground">
                  LV-Daten haben eine Hierarchie. Wähle wie die Gewerke in Softec angelegt werden sollen:
                </p>
                <RadioGroup
                  value={hierarchyMode}
                  onValueChange={(value: 'coarse' | 'granular') => setHierarchyMode(value)}
                  className="space-y-2"
                >
                  {/* Coarse option */}
                  <div className={`flex items-start space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${hierarchyMode === 'coarse' ? 'border-blue-500 bg-blue-50' : 'hover:bg-muted/50'}`}>
                    <RadioGroupItem value="coarse" id="coarse" className="mt-0.5" />
                    <Label htmlFor="coarse" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Grob</span>
                        <Badge variant="secondary" className="text-xs">
                          {previewData.coarseTradeCount} {previewData.coarseTradeCount === 1 ? 'Gewerk' : 'Gewerke'}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Hauptgewerke als Trades (z.B. "Elektro", "Sanitär")
                      </p>
                      {previewData.coarseTradeNames.length > 0 && (
                        <p className="text-xs text-blue-600 mt-1">
                          {previewData.coarseTradeNames.join(', ')}
                          {previewData.coarseTradeCount > 5 && '...'}
                        </p>
                      )}
                    </Label>
                  </div>

                  {/* Granular option */}
                  <div className={`flex items-start space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${hierarchyMode === 'granular' ? 'border-blue-500 bg-blue-50' : 'hover:bg-muted/50'}`}>
                    <RadioGroupItem value="granular" id="granular" className="mt-0.5" />
                    <Label htmlFor="granular" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Detailliert</span>
                        <Badge variant="secondary" className="text-xs">
                          {previewData.granularTradeCount} {previewData.granularTradeCount === 1 ? 'Gewerk' : 'Gewerke'}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Untergewerke als Trades (z.B. "Eigenstromversorgung", "Niederspannungsschaltanlagen")
                      </p>
                      {previewData.granularTradeNames.length > 0 && (
                        <p className="text-xs text-blue-600 mt-1">
                          {previewData.granularTradeNames.join(', ')}
                          {previewData.granularTradeCount > 5 && '...'}
                        </p>
                      )}
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}
            {isLoadingPreview && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Lade Vorschau...</span>
              </div>
            )}

            {/* Export Mode Selection */}
            <div className="space-y-3">
              <Label>Export-Modus</Label>
              <RadioGroup
                value={exportMode}
                onValueChange={(value: ExportMode) => setExportMode(value)}
                className="space-y-3"
              >
                {/* Option 1: Create new project */}
                <div className={`flex items-start space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${exportMode === 'create' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}>
                  <RadioGroupItem value="create" id="create" className="mt-0.5" />
                  <Label htmlFor="create" className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-2 font-medium">
                      <Plus className="h-4 w-4" />
                      Neues Projekt erstellen
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Erstellt ein neues Softec-Projekt mit den Angebotsdaten und importiert das Budget automatisch.
                    </p>

                    {/* Project title input - only shown when this mode is selected */}
                    {exportMode === 'create' && (
                      <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                        <Label htmlFor="project-title" className="text-xs text-muted-foreground">
                          Projektname in Softec
                        </Label>
                        <Input
                          id="project-title"
                          value={projectTitle}
                          onChange={(e) => setProjectTitle(e.target.value)}
                          placeholder="z.B. Neubau Müller, Hauptstraße 5"
                          className="mt-1"
                        />
                      </div>
                    )}
                  </Label>
                </div>

                {/* Option 2: Add to existing project */}
                <div className={`flex items-start space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${exportMode === 'existing' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}>
                  <RadioGroupItem value="existing" id="existing" className="mt-0.5" />
                  <Label htmlFor="existing" className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-2 font-medium">
                      <FolderOpen className="h-4 w-4" />
                      Zu bestehendem Projekt hinzufügen
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Fügt das Budget zu einem existierenden Softec-Projekt hinzu.
                    </p>

                    {/* Project selector - only shown when this mode is selected */}
                    {exportMode === 'existing' && (
                      <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={selectedProjectId || ''}
                          onValueChange={(value) => {
                            setSelectedProjectId(value);
                            const selectedProject = projects.find((project) => project.id === value);
                            setExistingStructureLabel(selectedProject?.name || '');
                          }}
                          disabled={isLoadingProjects}
                        >
                          <SelectTrigger className="w-full">
                            {isLoadingProjects ? (
                              <div className="flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Projekte werden geladen...</span>
                              </div>
                            ) : (
                              <SelectValue placeholder="Projekt auswählen..." />
                            )}
                          </SelectTrigger>
                          <SelectContent>
                            {projects.length === 0 ? (
                              <div className="p-2 text-sm text-muted-foreground text-center">
                                Keine Projekte gefunden
                              </div>
                            ) : (
                              projects.map((project) => (
                                <SelectItem key={project.id} value={project.id}>
                                  <div className="flex flex-col">
                                    <span className="font-medium">{project.name}</span>
                                    {project.clientName && (
                                      <span className="text-xs text-muted-foreground">
                                        {project.clientName}
                                      </span>
                                    )}
                                  </div>
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>

                        <div className="mt-2">
                          <Label htmlFor="existing-structure-label" className="text-xs text-muted-foreground">
                            Bezeichnung des neuen Auftrags
                          </Label>
                          <Input
                            id="existing-structure-label"
                            value={existingStructureLabel}
                            onChange={(e) => setExistingStructureLabel(e.target.value)}
                            placeholder="z.B. Nachtrag 1 - Elektro"
                            className="mt-1"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Diese Bezeichnung wird in Softec als Auftragsname gespeichert.
                          </p>
                        </div>
                      </div>
                    )}
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Info message - append mode, no overwrite */}
            {exportMode === 'existing' && selectedProjectId && existingBudgets.length > 0 && (
              <div className="rounded-lg border-2 border-green-500 bg-green-50 p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-green-800">
                      Neuer Auftrag wird hinzugefuegt
                    </h4>
                    <p className="text-sm text-green-700 mt-1">
                      Das Angebot wird als neuer Auftrag zum bestehenden Budget hinzugefuegt. Vorhandene Daten bleiben erhalten.
                    </p>

                    {/* Show existing budget info */}
                    {existingBudgets.filter(b => b.status === 'active').map((budget) => (
                      <div key={budget.id} className="mt-3 p-2 bg-green-100 rounded border border-green-300">
                        <div className="flex items-center gap-2 text-sm">
                          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300 text-xs">
                            Aktiv
                          </Badge>
                          <span className="font-medium text-green-800">
                            {budget.sourceOfferId || 'Unbenannt'}
                            {budget.sourceOfferVersion && ` (v${budget.sourceOfferVersion})`}
                          </span>
                          {budget.grossTotal && (
                            <span className="text-green-600 ml-auto">
                              {parseFloat(budget.grossTotal).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Show task count */}
                    {workData && workData.taskCount > 0 && (
                      <p className="text-xs text-green-600 mt-2">
                        {workData.taskCount} Positionen im bestehenden Budget
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Loading indicator for budget check */}
            {exportMode === 'existing' && selectedProjectId && isCheckingBudgets && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Prüfe vorhandene Budgets...</span>
              </div>
            )}

            {/* Connection Warning */}
            {status?.connected === false && (
              <div className="rounded-lg border p-4 bg-destructive/10">
                <p className="text-sm text-destructive">
                  Warnung: Verbindung zur Softec-API konnte nicht hergestellt werden.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-2">
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleConfirmExport}
              disabled={
                !status?.configured ||
                isCheckingBudgets ||
                (exportMode === 'existing' && !selectedProjectId)
              }
            >
              {exportMode === 'create' ? (
                <Plus className="h-4 w-4 mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {exportMode === 'create' ? 'Projekt erstellen' : 'An Softec senden'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SoftecExportButton;
